import React, { useRef, useState, useCallback } from "react";
import classNames from "classnames";
import { IconPDF } from "@wwg/icons";

import Select from "shared/@forms/Select";
import TextInput from "shared/@forms/TextInput";
import Input from "shared/@forms/Input";
import Button from "shared/Button";
import Card from "shared/Card";
import { Section } from "shared/layout";
import { FILE_TYPE } from "Marketing/CMS/enums";

import styles from "./FileUpload.module.css";
import DocIcon from "../../icons/Doc_Symbol_Icon.svg";
import PPTIcon from "../../icons/Powerpoint_Symbol_Icon.svg";
import KeyIcon from "../../components/FileTypeIcon/icons/keynote.svg";
import StockIcon from "../../icons/Thumbnail_Symbol.svg";

const FILE_TYPE_CONFIG = {
  [FILE_TYPE.pdf]: { label: "PDF", accept: ".pdf", extensions: [".pdf"] },
  [FILE_TYPE.document]: {
    label: "Word document",
    accept: ".doc,.docx",
    extensions: [".doc", ".docx"],
  },
  [FILE_TYPE.ppt]: {
    label: "PowerPoint presentation",
    accept: ".ppt,.pptx",
    extensions: [".ppt", ".pptx"],
  },
  [FILE_TYPE.pps]: {
    label: "PowerPoint Show",
    accept: ".pps,.ppsx",
    extensions: [".pps", ".ppsx"],
  },
  [FILE_TYPE.keynote]: {
    label: "Keynote",
    accept: ".key",
    extensions: [".key"],
  },
};

const normalizeLink = (value) => {
  const trimmedValue = value.trim();
  const hasScheme = /^[a-z][a-z\d+.-]*:/i.test(trimmedValue);
  const candidate = hasScheme ? trimmedValue : `https://${trimmedValue}`;

  try {
    const url = new URL(candidate);
    const hostname = url.hostname;
    const normalizedHostname = hostname.toLowerCase().replace(/\.$/, "");
    const isValidHostname =
      /^(?=.{1,253}$)(?:[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?)(?:\.(?:[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?))*$/i.test(
        normalizedHostname
      ) && normalizedHostname.includes(".");

    if (
      !["http:", "https:"].includes(url.protocol) ||
      !isValidHostname ||
      url.username ||
      url.password
    ) {
      return null;
    }
    return url.href;
  } catch {
    return null;
  }
};

export function FileUpload({ fileOptions, onFileChange }) {
  const upload = useRef(null);

  const [file, setFile] = useState(null);
  const [fileFormat, setFileFormat] = useState(0);
  const [fileError, setFileError] = useState("");
  const [linkError, setLinkError] = useState("");
  const [version, setVersion] = useState(1.0);
  const [path, setPath] = useState(null);

  const onSave = useCallback(() => {
    const normalizedPath =
      fileFormat === FILE_TYPE.url ? normalizeLink(path || "") : path;

    if (fileFormat === FILE_TYPE.url && !normalizedPath) {
      setLinkError(
        "Enter a valid link with a domain, such as https://www.example.com."
      );
      return;
    }

    onFileChange({
      file,
      fileFormat,
      version,
      path: normalizedPath,
    });

    setFile(null);
    setFileError("");
    setLinkError("");
    setFileFormat(0);
    setVersion(1.0);
    setPath(null);
    if (upload.current) {
      upload.current.value = "";
    }
  }, [file, fileFormat, version, path, onFileChange]);

  const onCancel = useCallback((e) => {
    e.preventDefault();
    setFile(null);
    setFileError("");
    setLinkError("");
    setFileFormat(0);
    setVersion(1.0);
    setPath(null);
    if (upload.current) {
      upload.current.value = "";
    }
  }, []);

  const onClick = useCallback((e) => {
    e.preventDefault();
    if (upload.current) {
      upload.current.click();
    }
  }, []);

  const onSubmit = useCallback(
    (e) => {
      e.preventDefault();
      onSave();
    },
    [onSave]
  );

  let remaining = 8;
  path && (remaining = remaining - path.length);

  const onSelectFile = (e) => {
    const selectedFile = e.target.files && e.target.files[0];
    const fileTypeConfig = FILE_TYPE_CONFIG[fileFormat];

    if (
      selectedFile &&
      fileTypeConfig &&
      !fileTypeConfig.extensions.some((extension) =>
        selectedFile.name.toLowerCase().endsWith(extension)
      )
    ) {
      setFile(null);
      setFileError(`Please select a ${fileTypeConfig.label} file.`);
      e.target.value = "";
      return;
    }

    setFile(selectedFile || null);
    setFileError("");
  };

  return (
    <Card>
      <Section>
        <header>
          <h3 className={classNames(styles.sectionTitle)}>Add New File</h3>
          <Select
            value={fileFormat}
            onChange={(e) => {
              setFileFormat(parseInt(e.target.value, 10));
              setFileError("");
              setLinkError("");
              setPath(null);
              setVersion(1.0);
              setFile(null);
              if (upload.current) {
                upload.current.value = "";
              }
            }}
          >
            <option value={0}>Select File Type</option>

            {fileOptions.map((option) => (
              <option value={option.id} key={option.id}>
                {option.id === FILE_TYPE.ppt ? "PowerPoint PPT" : option.label}
              </option>
            ))}
          </Select>
        </header>

        {fileError && (
          <span className={styles.fileError} role="alert">
            {fileError}
          </span>
        )}
        {linkError && (
          <span className={styles.fileError} role="alert">
            {linkError}
          </span>
        )}

        {fileFormat === 0 && (
          <span className={styles.empty}>Choose a file type</span>
        )}

        {fileFormat === FILE_TYPE.pdf && (
          <form className={styles.content} onSubmit={onSubmit}>
            {file && (
              <div className={styles.row}>
                <div className={styles.col}>
                  <div className={styles.row}>
                    <IconPDF size={45} />
                  </div>

                  <div className={styles.row}> {file.name}</div>
                </div>
              </div>
            )}

            <input
              type="file"
              name="file"
              id="upload"
              ref={upload}
              className={styles.file}
              accept={FILE_TYPE_CONFIG[fileFormat].accept}
              onChange={onSelectFile}
            />

            <div className={styles.row}>
              <div className={styles.col}>
                <div className={classNames(styles.row, styles.buttons)}>
                  <Button onClick={onCancel}>Cancel</Button>
                  <Button onClick={onClick}>Select A PDF</Button>
                  <VersionBox value={version} onChange={setVersion} />
                  <Button type="submit" disabled={isEmpty(file)}>
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </form>
        )}

        {fileFormat === FILE_TYPE.audio && (
          <form className={styles.content} onSubmit={onSubmit}>
            <div className={styles.row}>
              <Input
                className={styles.vzaarId}
                type="text"
                title="Dacast ID"
                value={path}
                placeholder={"Enter Vzaar ID"}
                onChange={(e) => setPath(e.target.value)}
              />

              <VersionBox value={version} onChange={setVersion} />
            </div>

            <div className={styles.row}>
              <div className={styles.col}>
                <div className={classNames(styles.row, styles.buttons)}>
                  <Button onClick={onCancel}>Cancel</Button>

                  <Button type="submit" disabled={isEmpty(path)}>
                    Save Audio
                  </Button>
                </div>
              </div>
            </div>
          </form>
        )}

        {fileFormat === FILE_TYPE.video && (
          <form className={styles.content} onSubmit={onSubmit}>
            <div className={styles.row}>
              <Input
                className={styles.vzaarId}
                type="text"
                title="Dacast ID"
                value={path}
                placeholder={"Enter Vzaar ID"}
                onChange={(e) => setPath(e.target.value)}
              />

              <VersionBox value={version} onChange={setVersion} />
            </div>

            <div className={styles.row}>
              <div className={styles.col}>
                <div className={classNames(styles.row, styles.buttons)}>
                  <Button onClick={onCancel}>Cancel</Button>

                  <Button type="submit" disabled={isEmpty(path)}>
                    Save Video
                  </Button>
                </div>
              </div>
            </div>
          </form>
        )}

        {fileFormat === FILE_TYPE.document && (
          <form className={styles.content} onSubmit={onSubmit}>
            {file && (
              <div className={styles.row}>
                <div className={styles.col}>
                  <div className={styles.row}>
                    <img src={DocIcon} alt="DOC" />
                  </div>

                  <div className={styles.row}> {file.name}</div>
                </div>
              </div>
            )}

            <input
              type="file"
              name="file"
              id="upload"
              ref={upload}
              className={styles.file}
              accept={FILE_TYPE_CONFIG[fileFormat].accept}
              onChange={onSelectFile}
            />

            <div className={styles.row}>
              <div className={styles.col}>
                <div className={classNames(styles.row, styles.buttons)}>
                  <Button onClick={onCancel}>Cancel</Button>
                  <Button onClick={onClick}>Select Document</Button>
                  <VersionBox value={version} onChange={setVersion} />
                  <Button type="submit" disabled={isEmpty(file)}>
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </form>
        )}

        {fileFormat === FILE_TYPE.ppt && (
          <form className={styles.content} onSubmit={onSubmit}>
            {file && (
              <div className={styles.row}>
                <div className={styles.col}>
                  <div className={styles.row}>
                    <img src={PPTIcon} alt="PPT" />
                  </div>

                  <div className={styles.row}> {file.name}</div>
                </div>
              </div>
            )}

            <input
              type="file"
              name="file"
              id="upload"
              ref={upload}
              className={styles.file}
              accept={FILE_TYPE_CONFIG[fileFormat].accept}
              onChange={onSelectFile}
            />

            <div className={styles.row}>
              <div className={styles.col}>
                <VersionBox value={version} onChange={setVersion} />

                <div className={classNames(styles.row, styles.buttons)}>
                  <Button onClick={onCancel}>Cancel</Button>
                  <Button onClick={onClick}>Select Presentation</Button>
                  <Button type="submit" disabled={isEmpty(file)}>
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </form>
        )}

        {fileFormat === FILE_TYPE.pps && (
          <form className={styles.content} onSubmit={onSubmit}>
            {file && (
              <div className={styles.row}>
                <div className={styles.col}>
                  <div className={styles.row}>
                    <img src={PPTIcon} alt="PPS" />
                  </div>

                  <div className={styles.row}> {file.name}</div>
                </div>
              </div>
            )}

            <input
              type="file"
              name="file"
              id="upload"
              ref={upload}
              className={styles.file}
              accept={FILE_TYPE_CONFIG[fileFormat].accept}
              onChange={onSelectFile}
            />

            <div className={styles.row}>
              <div className={styles.col}>
                <VersionBox value={version} onChange={setVersion} />

                <div className={classNames(styles.row, styles.buttons)}>
                  <Button onClick={onCancel}>Cancel</Button>
                  <Button onClick={onClick}>Select Presentation</Button>
                  <Button type="submit" disabled={isEmpty(file)}>
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </form>
        )}

        {fileFormat === FILE_TYPE.keynote && (
          <form className={styles.content} onSubmit={onSubmit}>
            {file && (
              <div className={styles.row}>
                <div className={styles.col}>
                  <div className={styles.row}>
                    <img src={KeyIcon} alt="Keynote" />
                  </div>

                  <div className={styles.row}> {file.name}</div>
                </div>
              </div>
            )}

            <input
              type="file"
              name="file"
              id="upload"
              ref={upload}
              className={styles.file}
              accept={FILE_TYPE_CONFIG[fileFormat].accept}
              onChange={onSelectFile}
            />

            <div className={styles.row}>
              <div className={styles.col}>
                <div className={classNames(styles.row, styles.buttons)}>
                  <Button onClick={onCancel}>Cancel</Button>
                  <Button onClick={onClick}>Select Keynote</Button>
                  <VersionBox value={version} onChange={setVersion} />
                  <Button type="submit" disabled={isEmpty(file)}>
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </form>
        )}

        {fileFormat === FILE_TYPE.url && (
          <form className={styles.content} onSubmit={onSubmit}>
            <div className={styles.row}>
              <TextInput
                placeholder="http://"
                title="Enter a web address; HTTPS will be added if omitted"
                value={path}
                type="text"
                onChange={(e) => {
                  setPath(e.target.value);
                  setLinkError("");
                }}
              />
            </div>

            <div className={styles.row}>
              <div className={styles.col}>
                <div className={classNames(styles.row, styles.buttons)}>
                  <Button onClick={onCancel}>Cancel</Button>

                  <VersionBox value={version} onChange={setVersion} />

                  <Button type="submit" disabled={isEmpty(path)}>
                    Save Link
                  </Button>
                </div>
              </div>
            </div>
          </form>
        )}

        {fileFormat === 9 && (
          <form className={styles.content} onSubmit={onSubmit}>
            {file && (
              <div className={styles.row}>
                <div className={styles.col}>
                  <div className={styles.row}>
                    <img src={StockIcon} alt="Other" />
                  </div>

                  <div className={styles.row}> {file.name}</div>
                </div>
              </div>
            )}

            <input
              type="file"
              name="file"
              id="upload"
              ref={upload}
              className={styles.file}
              accept={
                FILE_TYPE_CONFIG[fileFormat]
                  ? FILE_TYPE_CONFIG[fileFormat].accept
                  : undefined
              }
              onChange={onSelectFile}
            />

            <div className={styles.row}>
              <div className={styles.col}>
                <div className={classNames(styles.row, styles.buttons)}>
                  <Button onClick={onCancel}>Cancel</Button>
                  <Button onClick={onClick}>Select File</Button>
                  <VersionBox value={version} onChange={setVersion} />
                  <Button type="submit" disabled={isEmpty(file)}>
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </form>
        )}
      </Section>
    </Card>
  );
}

FileUpload.propTypes = {};

function VersionBox({ value, onChange }) {
  return (
    <div className={styles.versionBox}>
      Version:
      <Input
        type="number"
        maxLength="3"
        step=".1"
        value={String(value)}
        onChange={(e) => {
          onChange(Number(e.target.value));
        }}
        className={styles.version}
      />
    </div>
  );
}

const isNumber = (value) => !isNaN(+value);
const isEmpty = (value) =>
  typeof value === "string" ? value.trim() === "" : value == null;
